import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SOLUTIONS } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

export default function Solutions() {
  const [active, setActive] = useState(0);
  const current = SOLUTIONS[active];

  return (
    <section
      id="solutions"
      className="relative bg-obsidian py-24 md:py-32 border-t border-white/10"
      data-testid="solutions-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-14"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-spartangreen" />
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-spartangreen">
              Our Products &amp; Solutions
            </span>
          </div>
          <h2 className="font-display uppercase tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            A machine for
            <span className="text-spartanorange"> every line</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left: category selector */}
          <div className="lg:col-span-6" data-testid="solutions-selector">
            {SOLUTIONS.map((s, i) => (
              <button
                key={s.title}
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
                data-testid={`solution-tab-${i}`}
                className={`group w-full text-left border-b border-white/10 first:border-t py-7 md:py-9 transition-colors duration-300 ${
                  active === i ? "" : "opacity-55 hover:opacity-100"
                }`}
              >
                <div className="flex items-baseline gap-5">
                  <span className="font-mono text-xs text-spartanorange">
                    0{i + 1}
                  </span>
                  <h3
                    className={`font-display uppercase tracking-tight text-3xl md:text-5xl transition-colors duration-300 ${
                      active === i ? "text-spartanorange" : "text-white"
                    }`}
                  >
                    {s.title}
                  </h3>
                </div>
                <AnimatePresence initial={false}>
                  {active === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="pt-5 pl-9 flex flex-wrap gap-2">
                        {s.machines.map((m) => (
                          <span
                            key={m}
                            className="font-mono text-[11px] tracking-wide uppercase text-white/70 border border-white/15 px-3 py-1.5"
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            ))}
          </div>

          {/* Right: sticky spotlight image */}
          <div className="lg:col-span-6 lg:sticky lg:top-28">
            <div className="relative clip-corner overflow-hidden border border-white/10 aspect-[4/5] md:aspect-square">
              <AnimatePresence mode="wait">
                <motion.img
                  key={current.image}
                  src={current.image}
                  alt={current.title}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              {/* spotlight overlay */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 50% 35%, rgba(10,10,10,0) 0%, rgba(10,10,10,0.55) 75%, rgba(10,10,10,0.85) 100%)",
                }}
              />
              <div className="absolute top-5 left-5 font-mono text-xs tracking-[0.2em] uppercase text-white/80">
                {String(active + 1).padStart(2, "0")} / {SOLUTIONS.length}
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-display uppercase tracking-tight text-4xl md:text-5xl text-white">
                  {current.title}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
